import { ArrowsClockwise, MagnifyingGlass, PlusCircle, Warning } from "@phosphor-icons/react";
import { Button, Spinner, TextInput } from "flowbite-react";
import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, useAvailableAddons } from "../../../../hooks";
import { useIntegrationApps } from "../../../../hooks/get/useIntegrationApps";
import { useUpdateAppSelection } from "../../../../hooks/patch/useUpdateAppSelection";
import { IAppSelectionLimitError, IIntegrationApp } from "../../../../interfaces";

const POLL_INTERVAL_MS = 60 * 60 * 1000; // 1 hour
const POLL_TIMEOUT_MS = 24 * 60 * 60 * 1000; // 24 hours

function getSyncBadge(app: IIntegrationApp): React.ReactNode {
    const { is_sync_on, sync_status, initial_sync_completed } = app;

    if (!is_sync_on && !initial_sync_completed) return null;

    if (!is_sync_on && initial_sync_completed) {
        return (
            <span
                className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-amber-50 text-amber-700 border border-amber-200"
                title="This app has historical data but is no longer syncing daily. It was replaced by other active apps."
            >
                Historical
            </span>
        );
    }

    if (is_sync_on && sync_status === "pending") {
        return (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-yellow-100 text-yellow-700">
                Queued
            </span>
        );
    }

    if (is_sync_on && sync_status === "in_progress") {
        return (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700 flex items-center gap-1">
                <ArrowsClockwise size={10} className="animate-spin" />
                Syncing…
            </span>
        );
    }

    if (is_sync_on && sync_status === "completed" && initial_sync_completed) {
        return (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-700">
                Synced ✓
            </span>
        );
    }

    if (is_sync_on && sync_status === "failed") {
        return (
            <span
                className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-red-100 text-red-600 flex items-center gap-1"
                title="Sync failed, retrying automatically"
            >
                <Warning size={10} />
                Retry…
            </span>
        );
    }

    return null;
}

const AppSelection: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isVip, selectedIntegration, subscriptionDetails } = useAuth();
    const { data: availableAddons } = useAvailableAddons();

    const isUnlimited = isVip;
    const maxApps = isUnlimited ? -1 : (subscriptionDetails?.overrides?.max_apps ?? subscriptionDetails?.plan_limits?.max_apps ?? 0);
    const hasAddonsAvailable = (availableAddons?.length ?? 0) > 0;

    const trackingId = selectedIntegration?.value;

    const [pollStart] = useState(() => Date.now());
    const [shouldPoll, setShouldPoll] = useState(false);

    const { data, isLoading } = useIntegrationApps(trackingId, shouldPoll ? POLL_INTERVAL_MS : false);
    const { mutate: updateSelection, isPending: isSaving } = useUpdateAppSelection();

    const [localSelections, setLocalSelections] = useState<Record<number, boolean>>({});
    const [searchQuery, setSearchQuery] = useState("");
    const [limitWarning, setLimitWarning] = useState(false);

    // Start/stop polling based on sync state
    useEffect(() => {
        if (!data?.apps) return;

        const hasPendingSync = data.apps.some(
            (a) => a.is_sync_on && !a.initial_sync_completed
        );
        const timedOut = Date.now() - pollStart > POLL_TIMEOUT_MS;

        setShouldPoll(hasPendingSync && !timedOut);
    }, [data, pollStart]);

    // Sync local state when data arrives or integration changes
    useEffect(() => {
        if (data?.apps) {
            const initial: Record<number, boolean> = {};
            data.apps.forEach((app) => {
                initial[app.id] = app.is_sync_on;
            });
            setLocalSelections(initial);
            setLimitWarning(false);
        }
    }, [data]);

    const selectedCount = useMemo(
        () => Object.values(localSelections).filter(Boolean).length,
        [localSelections]
    );

    const filteredApps = useMemo(() => {
        if (!data?.apps) return [];
        if (!searchQuery.trim()) return data.apps;
        const q = searchQuery.toLowerCase();
        return data.apps.filter(
            (app) =>
                app.google_admob_app_name.toLowerCase().includes(q) ||
                app.google_admob_store_id.toLowerCase().includes(q)
        );
    }, [data, searchQuery]);

    const hasChanges = useMemo(() => {
        if (!data?.apps) return false;
        return data.apps.some((app) => localSelections[app.id] !== app.is_sync_on);
    }, [data, localSelections]);

    const allApps = data?.apps ?? [];
    const allSelected = allApps.length > 0 && allApps.every((a) => localSelections[a.id] ?? false);

    const handleSelectAll = () => {
        if (allSelected) {
            setLocalSelections((prev) => {
                const next = { ...prev };
                allApps.forEach((a) => { next[a.id] = false; });
                return next;
            });
            setLimitWarning(false);
            return;
        }

        const slots = isUnlimited ? Infinity : maxApps;
        let filled = 0;
        const next = { ...localSelections };
        for (const app of allApps) {
            if (app.sync_status === "in_progress") continue;
            if (filled >= slots) break;
            next[app.id] = true;
            filled++;
        }
        setLocalSelections(next);
        if (!isUnlimited && allApps.filter((a) => a.sync_status !== "in_progress").length > slots) {
            setLimitWarning(true);
        }
    };

    const handleToggle = (app: IIntegrationApp) => {
        // Disable toggle while actively syncing
        if (app.sync_status === "in_progress") return;

        const currentlyOn = localSelections[app.id] ?? false;
        if (!currentlyOn && !isUnlimited && selectedCount >= maxApps) {
            setLimitWarning(true);
            return;
        }
        setLimitWarning(false);
        setLocalSelections((prev) => ({ ...prev, [app.id]: !currentlyOn }));
    };

    const handleSave = () => {
        if (!trackingId) return;
        const originalMap = Object.fromEntries((data?.apps ?? []).map((a) => [a.id, a.is_sync_on]));
        const apps = Object.entries(localSelections)
            .filter(([id, is_sync_on]) => originalMap[Number(id)] !== is_sync_on)
            .map(([id, is_sync_on]) => ({ id: Number(id), is_sync_on }));
        updateSelection(
            { tracking_id: trackingId, apps },
            {
                onSuccess: (result) => {
                    toast.success(
                        `Updated: ${result.activated} activated, ${result.deactivated} deactivated.`
                    );
                },
                onError: (err: unknown) => {
                    const error = err as { response?: { data?: IAppSelectionLimitError }; message?: string };
                    const body = error?.response?.data;
                    if (body?.available_slots !== undefined) {
                        toast.error(
                            `Plan limit reached. You have ${body.available_slots} slot(s) remaining.`
                        );
                    } else {
                        toast.error(error?.message || "Failed to update app selection.");
                    }
                },
            }
        );
    };

    if (!trackingId) {
        return (
            <div className="flex flex-col items-center justify-center py-24 text-gray-400">
                <p className="text-sm font-medium">Select an integration pair from the dropdown above to manage its apps.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-bold text-gray-900">App Selection</h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                        Choose which apps to sync for <span className="font-medium text-gray-700">{selectedIntegration?.label}</span>.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {/* Polling indicator */}
                    {shouldPoll && (
                        <span className="flex items-center gap-1.5 text-xs text-blue-600">
                            <ArrowsClockwise size={13} className="animate-spin" />
                            Live sync updates…
                        </span>
                    )}
                    {/* Plan limit badge */}
                    <span className={`text-sm font-medium px-3 py-1 rounded-full ${isUnlimited
                        ? "bg-emerald-50 text-emerald-700"
                        : selectedCount >= maxApps
                            ? "bg-red-50 text-red-600"
                            : "bg-purple-50 text-purple-700"
                        }`}>
                        {isUnlimited
                            ? `${selectedCount} selected · Unlimited`
                            : `${selectedCount} / ${maxApps} selected`}
                    </span>
                    <Button
                        color="light"
                        onClick={handleSelectAll}
                        disabled={isSaving || allApps.length === 0}
                        size="sm"
                    >
                        {allSelected ? "Deselect All" : "Select All"}
                    </Button>
                    <Button
                        color="success"
                        onClick={handleSave}
                        disabled={!hasChanges || isSaving}
                        isProcessing={isSaving}
                        size="sm"
                    >
                        Save Selection
                    </Button>
                </div>
            </div>

            {/* Plan limit warning */}
            {limitWarning && !isUnlimited && (
                <div className="px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-xl text-sm text-amber-800 dark:text-amber-300 flex flex-col sm:flex-row sm:items-center gap-3">
                    <span className="flex-1">
                        {t("appSelection.limitReached", "You've used all {{n}} app slots.", { n: maxApps })}{" "}
                        {t("appSelection.limitHint", "Deselect an app first to make room.")}
                    </span>
                    {hasAddonsAvailable && (
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                                onClick={() => navigate("/billing")}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
                            >
                                <PlusCircle size={14} />
                                {t("appSelection.buyMoreSlots", "Buy more app slots")}
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Search */}
            <div className="max-w-sm">
                <TextInput
                    icon={MagnifyingGlass}
                    placeholder="Search apps by name or store ID…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </div>

            {/* App Grid */}
            {isLoading ? (
                <div className="flex items-center justify-center py-20">
                    <Spinner size="xl" color="purple" />
                </div>
            ) : filteredApps.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {filteredApps.map((app) => {
                        const isOn = localSelections[app.id] ?? false;
                        const isSyncing = app.sync_status === "in_progress";
                        const wouldExceedLimit = !isOn && !isUnlimited && selectedCount >= maxApps;
                        const isDisabled = isSyncing || wouldExceedLimit;
                        const syncBadge = getSyncBadge(app);

                        return (
                            <button
                                key={app.id}
                                onClick={() => handleToggle(app)}
                                disabled={isDisabled}
                                title={isSyncing ? "Sync in progress — please wait" : undefined}
                                className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-all w-full ${isOn
                                    ? "border-emerald-500 bg-emerald-50 shadow-sm"
                                    : isDisabled
                                        ? "border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed"
                                        : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                                    }`}
                            >
                                {/* App Icon */}
                                <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
                                    {app.google_admob_app_logo ? (
                                        <img
                                            src={app.google_admob_app_logo}
                                            alt={app.google_admob_app_name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs font-bold">
                                            {app.google_admob_app_name.charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                </div>

                                {/* App Info */}
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-800 truncate">
                                        {app.google_admob_app_name}
                                    </p>
                                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                        <span className="text-xs text-gray-500 truncate max-w-[80px]">
                                            {app.google_admob_store_id}
                                        </span>
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${app.google_admob_app_platform === "IOS"
                                            ? "bg-gray-100 text-gray-600"
                                            : "bg-green-100 text-green-700"
                                            }`}>
                                            {app.google_admob_app_platform}
                                        </span>
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${app.google_admob_app_status === "Published"
                                            ? "bg-emerald-50 text-emerald-700"
                                            : "bg-gray-100 text-gray-500"
                                            }`}>
                                            {app.google_admob_app_status}
                                        </span>
                                        {syncBadge}
                                    </div>
                                </div>

                                {/* Checkbox */}
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isOn
                                    ? "bg-emerald-500 border-emerald-500"
                                    : "bg-white border-gray-300"
                                    }`}>
                                    {isOn && (
                                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 12 12">
                                            <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                    <p className="text-sm font-medium">
                        {searchQuery ? "No apps match your search." : "No apps found for this integration."}
                    </p>
                </div>
            )}
        </div>
    );
};

export default AppSelection;
